import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  FileCopy,
  Publish,
  Archive,
  Analytics,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTrainingManagement } from '../hooks/useTrainingManagement';
import { TrainingCourse } from '../services/trainingManagementService';
import CourseDeleteModal from './CourseDeleteModal';

interface CourseGridProps {
  courses: TrainingCourse[];
  onRefresh: () => void;
}

export default function CourseGrid({ courses, onRefresh }: CourseGridProps) {
  const navigate = useNavigate();
  const { publishCourse, archiveCourse, duplicateCourse } = useTrainingManagement();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCourse, setSelectedCourse] = useState<TrainingCourse | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, course: TrainingCourse) => {
    setAnchorEl(event.currentTarget);
    setSelectedCourse(course);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCourse(null);
  };

  const handleEdit = () => {
    if (selectedCourse) {
      navigate(`/admin/training/courses/${selectedCourse.id}`);
    }
    handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (!selectedCourse) return;

    setActionLoading(true);
    setActionError(null);
    try {
      await duplicateCourse(selectedCourse.id);
      onRefresh();
      alert('Course duplicated successfully!');
    } catch (err: any) {
      setActionError(err.message || 'Failed to duplicate course');
    } finally {
      setActionLoading(false);
    }
    handleMenuClose();
  };

  const handlePublish = async () => {
    if (!selectedCourse) return;

    setActionLoading(true);
    setActionError(null);
    try {
      await publishCourse(selectedCourse.id);
      onRefresh();
      alert('Course published successfully!');
    } catch (err: any) {
      setActionError(err.message || 'Failed to publish course');
    } finally {
      setActionLoading(false);
    }
    handleMenuClose();
  };

  const handleArchive = async () => {
    if (!selectedCourse) return;

    setActionLoading(true);
    setActionError(null);
    try {
      await archiveCourse(selectedCourse.id);
      onRefresh();
      alert('Course archived successfully!');
    } catch (err: any) {
      setActionError(err.message || 'Failed to archive course');
    } finally {
      setActionLoading(false);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  const handleViewAnalytics = () => {
    if (selectedCourse) {
      navigate(`/admin/training/courses/${selectedCourse.id}/analytics`);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'ARCHIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  if (courses.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No courses found
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Create your first training course to get started
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/admin/training/courses/new')}
        >
          Create First Course
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Enrollments</TableCell>
              <TableCell align="center">Completion Rate</TableCell>
              <TableCell align="center">Certificates</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {courses.map((course) => (
              <TableRow key={course.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {course.title}
                  </Typography>
                  {course.isRequired && (
                    <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                  )}
                  {course.isRecommended && (
                    <Chip label="Recommended" size="small" color="warning" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{course.category || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={course.status}
                    size="small"
                    color={getStatusColor(course.status) as any}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{course.enrollmentCount || 0}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {course.completionRate !== undefined ? `${course.completionRate}%` : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{course.certificateCount || 0}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(course.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, course)}
                    disabled={actionLoading}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleViewAnalytics}>
          <ListItemIcon>
            <Analytics fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Analytics</ListItemText>
        </MenuItem>

        {selectedCourse?.status === 'DRAFT' && (
          <MenuItem onClick={handlePublish}>
            <ListItemIcon>
              <Publish fontSize="small" />
            </ListItemIcon>
            <ListItemText>Publish</ListItemText>
          </MenuItem>
        )}

        {selectedCourse?.status === 'PUBLISHED' && (
          <MenuItem onClick={handleArchive}>
            <ListItemIcon>
              <Archive fontSize="small" />
            </ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <FileCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Modal */}
      {selectedCourse && (
        <CourseDeleteModal
          open={deleteModalOpen}
          course={selectedCourse}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedCourse(null);
          }}
          onSuccess={() => {
            setDeleteModalOpen(false);
            setSelectedCourse(null);
            onRefresh();
          }}
        />
      )}
    </Box>
  );
}

